<template>
	<div class="min-h-screen bg-gray-50 py-4 sm:py-8">
		<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			<!-- Main Card -->
			<div
				v-if="underwriter"
				class="bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
			>
				<!-- Back Button -->
				<div class="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 lg:pt-8">
					<button
						@click="goBack"
						class="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors touch-manipulation active:scale-95"
					>
						<svg
							class="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 19l-7-7 7-7"
							/>
						</svg>
						Back
					</button>
				</div>

				<div class="flex flex-col lg:flex-row">
					<!-- LEFT SIDEBAR -->
					<aside
						class="w-full lg:w-2/5 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 sm:p-6 lg:p-10"
					>
						<div class="space-y-5 sm:space-y-6 lg:space-y-8">
							<!-- Logo & Name -->
							<div
								class="flex items-start sm:items-center gap-3 sm:gap-4"
							>
								<div class="relative flex-shrink-0">
									<img
										:src="logoUrl"
										:alt="
											underwriter.name ||
											'Underwriter logo'
										"
										loading="lazy"
										decoding="async"
										class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-xl sm:rounded-2xl object-contain shadow-lg border-2 sm:border-4 border-white ring-2 sm:ring-4 ring-blue-100"
										@error="fallbackLogo = true"
									/>
									<div
										v-if="statusBadge"
										class="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-bold text-white rounded-full shadow-lg status-badge"
										:class="statusBadgeClass"
									>
										{{ underwriter.status }}
									</div>
								</div>

								<div class="flex-1 min-w-0">
									<h1
										class="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 leading-tight break-words"
									>
										{{
											underwriter.name ||
											'Unnamed Underwriter'
										}}
									</h1>
									<p
										class="text-blue-600 font-medium mt-1 text-xs sm:text-sm lg:text-base"
									>
										{{ underwriter.category }}
									</p>
								</div>
							</div>

							<!-- Rating -->
							<div>
								<p
									class="text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2 sm:mb-3"
								>
									Claims Rating
								</p>
								<div class="flex items-center gap-2 sm:gap-3">
									<div class="flex gap-0.5 sm:gap-1">
										<svg
											v-for="n in 5"
											:key="n"
											class="w-5 h-5 sm:w-6 sm:h-6"
											:class="
												n <= roundedRating
													? 'text-yellow-400'
													: 'text-gray-300'
											"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L3.98 8.72c-.783-.57-.38-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z"
											/>
										</svg>
									</div>
									<span
										class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800"
										>{{ roundedRating }}</span
									>
									<span
										class="text-sm sm:text-base text-gray-500"
										>/ 5</span
									>
								</div>
							</div>

							<!-- Status Badges Grid -->
							<div class="grid grid-cols-2 gap-2 sm:gap-3">
								<div
									class="bg-white/90 backdrop-blur rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200"
								>
									<p
										class="text-[10px] sm:text-xs text-gray-500 font-medium mb-1"
									>
										KYC Status
									</p>
									<p
										class="text-sm sm:text-base font-bold truncate"
										:class="kycStatusClass"
									>
										{{ underwriter.kycStatus || 'Pending' }}
									</p>
								</div>
								<div
									class="bg-white/90 backdrop-blur rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200"
								>
									<p
										class="text-[10px] sm:text-xs text-gray-500 font-medium mb-1"
									>
										Registration
									</p>
									<p
										class="text-sm sm:text-base font-bold truncate"
										:class="regStatusClass"
									>
										{{
											underwriter.registrationStatus ||
											'N/A'
										}}
									</p>
								</div>
							</div>

							<!-- Codes -->
							<div
								class="bg-white/80 backdrop-blur rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-5 shadow-inner border border-gray-100"
							>
								<h4
									class="text-sm sm:text-base font-semibold text-gray-700 mb-2 sm:mb-3"
								>
									Identification Codes
								</h4>
								<div class="space-y-2 text-xs sm:text-sm">
									<div class="flex justify-between gap-2">
										<span
											class="text-gray-500 flex-shrink-0"
											>Group Code</span
										>
										<span
											class="font-mono font-bold break-all text-right"
											>{{
												underwriter.group_code || '—'
											}}</span
										>
									</div>
									<div class="flex justify-between gap-2">
										<span
											class="text-gray-500 flex-shrink-0"
											>System Code</span
										>
										<span
											class="font-mono font-bold break-all text-right"
											>{{ underwriter.code || '—' }}</span
										>
									</div>
									<div class="flex justify-between gap-2">
										<span
											class="text-gray-500 flex-shrink-0"
											>IRA Code</span
										>
										<span
											class="font-mono font-bold break-all text-right"
											>{{
												underwriter.ira_code || '—'
											}}</span
										>
									</div>
								</div>
							</div>
						</div>
					</aside>

					<!-- RIGHT CONTENT -->
					<div
						class="w-full lg:w-3/5 p-4 sm:p-6 lg:p-10 xl:p-12 bg-gray-50/30 space-y-6 sm:space-y-8 lg:space-y-10"
					>
						<!-- Company Details -->
						<section>
							<h3
								class="text-base sm:text-lg lg:text-xl font-bold text-gray-800 mb-3 sm:mb-4 lg:mb-5 flex items-center gap-2 sm:gap-3"
							>
								<svg
									class="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h-4m-6 0H5"
									/>
								</svg>
								Company Details
							</h3>
							<div
								class="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 lg:p-6 space-y-4"
							>
								<div
									class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 text-gray-700"
								>
									<div class="space-y-1">
										<p
											class="text-xs sm:text-sm text-gray-500"
										>
											Legal Name
										</p>
										<p
											class="text-sm sm:text-base font-semibold text-gray-900 break-words"
										>
											{{
												(underwriter.kyc &&
													underwriter.kyc
														.underwriterDetails &&
													underwriter.kyc
														.underwriterDetails
														.names) ||
												'—'
											}}
										</p>
									</div>
									<div class="space-y-1">
										<p
											class="text-xs sm:text-sm text-gray-500"
										>
											KRA PIN
										</p>
										<p
											class="text-sm sm:text-base font-mono font-semibold break-all"
										>
											{{
												(underwriter.kyc &&
													underwriter.kyc
														.underwriterDetails &&
													underwriter.kyc
														.underwriterDetails
														.kraPin) ||
												'—'
											}}
										</p>
									</div>
									<div class="space-y-1">
										<p
											class="text-xs sm:text-sm text-gray-500"
										>
											Email
										</p>
										<p
											class="text-sm sm:text-base font-semibold text-blue-600 break-all"
										>
											{{
												(underwriter.kyc &&
													underwriter.kyc
														.underwriterDetails &&
													underwriter.kyc
														.underwriterDetails
														.email) ||
												'—'
											}}
										</p>
									</div>
									<div class="space-y-1">
										<p
											class="text-xs sm:text-sm text-gray-500"
										>
											Physical Address
										</p>
										<p
											class="text-sm sm:text-base font-semibold break-words"
										>
											{{
												(underwriter.kyc &&
													underwriter.kyc
														.underwriterDetails &&
													underwriter.kyc
														.underwriterDetails
														.address) ||
												'—'
											}}
										</p>
									</div>
								</div>
							</div>
						</section>

						<!-- Regulatory Documents -->
						<!-- <section>
              <h3 class="text-base sm:text-lg lg:text-xl font-bold text-gray-800 mb-3 sm:mb-4 lg:mb-5 flex items-center gap-2 sm:gap-3">
                <svg class="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Regulatory Documents
              </h3>
              <div v-if="hasRegDocs" class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <a
                    v-for="(doc, i) in underwriter.kyc.regulatoryDocs"
                    :key="i"
                    :href="doc.path"
                    target="_blank"
                    class="flex items-center justify-between bg-white hover:bg-green-50 border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-5 transition-all hover:shadow-md group touch-manipulation active:scale-[0.98]"
                >
                  <div class="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div class="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg class="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span class="text-xs sm:text-sm lg:text-base font-medium text-gray-800 group-hover:text-green-700 break-words line-clamp-2">{{ doc.name }}</span>
                  </div>
                  <svg class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-green-600 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
              <div v-else class="text-center py-8 sm:py-10 lg:py-12 bg-gray-50 rounded-lg sm:rounded-xl border-2 border-dashed border-gray-300">
                <svg class="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p class="text-sm sm:text-base text-gray-500">No regulatory documents uploaded</p>
              </div>
            </section> -->

						<!-- Branches -->
						<section v-if="hasBranchDetails">
							<h3
								class="text-base sm:text-lg lg:text-xl font-bold text-gray-800 mb-3 sm:mb-4"
							>
								Branch Network
							</h3>
							<div
								class="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2"
							>
								<div
									v-for="(branch, i) in underwriter.kyc
										.branchDetails"
									:key="i"
									class="bg-white border border-gray-200 rounded-lg sm:rounded-xl p-4 sm:p-5 lg:p-6 shadow-sm hover:shadow-md transition-shadow"
								>
									<h4
										class="text-sm sm:text-base lg:text-lg font-bold text-gray-900 mb-2 sm:mb-3 break-words"
									>
										{{ branch.name }}
									</h4>
									<div
										class="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600"
									>
										<p class="break-words">
											<span
												class="font-medium text-gray-800"
												>Location:</span
											>
											{{ branch.location }}
										</p>
										<p class="break-all">
											<span
												class="font-medium text-gray-800"
												>Email:</span
											>
											{{ branch.email }}
										</p>
										<p class="break-words">
											<span
												class="font-medium text-gray-800"
												>Address:</span
											>
											{{ branch.address }}
										</p>
									</div>
								</div>
							</div>
						</section>

						<!-- MOU -->
						<section v-if="underwriter.mou">
							<h3
								class="text-base sm:text-lg lg:text-xl font-bold text-gray-800 mb-3 sm:mb-4 lg:mb-5 flex items-center gap-2 sm:gap-3"
							>
								<svg
									class="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									/>
								</svg>
								Memorandum of Understanding
							</h3>
							<a
								:href="underwriter.mou"
								target="_blank"
								class="flex w-full justify-center items-center gap-2 sm:gap-3 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white font-medium py-3 sm:py-3.5 px-5 sm:px-6 rounded-lg sm:rounded-xl transition-all shadow-lg hover:shadow-xl text-sm sm:text-base touch-manipulation active:scale-[0.98]"
							>
								<svg
									class="w-5 h-5"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 10v6m-4-3h8m5 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									/>
								</svg>
								Download MOU
							</a>
						</section>
					</div>
				</div>
			</div>

			<!-- Loading / Empty State -->
			<div
				v-else
				class="flex items-center justify-center h-64 sm:h-80 lg:h-96 bg-white rounded-xl sm:rounded-2xl shadow-xl border border-gray-200"
			>
				<div class="text-center px-4">
					<svg
						class="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h-4m-6 0H5"
						/>
					</svg>
					<p class="text-gray-500 text-sm sm:text-base lg:text-lg">
						Select an underwriter to view details
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
	name: 'ViewUnderwriter',
	props: {
		underwriterProp: { type: Object, default: null },
		underwriterData: { type: Object, default: null },
		productsData: { type: Array, default: () => [] },
	},
	data() {
		return {
			fallbackLogo: false,
			products: Array.isArray(this.productsData) ? this.productsData : [],
			sampleUnderwriter: null,
		};
	},
	computed: {
		...mapGetters('help', ['selectedEntity']),
		underwriter() {
			return (
				this.underwriterProp ||
				this.underwriterData ||
				this.selectedEntity ||
				this.sampleUnderwriter ||
				{}
			);
		},
		logoUrl() {
			if (this.fallbackLogo) return '/assets/Logo/nexus.svg';
			return (
				(this.underwriter &&
					(this.underwriter.logo ||
						(this.underwriter.kyc &&
							this.underwriter.kyc.underwriterDetails &&
							this.underwriter.kyc.underwriterDetails
								.companyLogo &&
							this.underwriter.kyc.underwriterDetails.companyLogo
								.path))) ||
				'/assets/Logo/nexus.svg'
			);
		},
		roundedRating() {
			const r = (this.underwriter && this.underwriter.rating) || 0;
			return Math.round((r || 0) * 10) / 10;
		},
		statusBadge() {
			const s =
				this.underwriter && this.underwriter.status
					? String(this.underwriter.status).toLowerCase()
					: '';
			return s && s !== 'active';
		},
		statusBadgeClass() {
			const s =
				this.underwriter && this.underwriter.status
					? String(this.underwriter.status).toLowerCase()
					: '';
			return {
				'bg-green-500': s === 'active',
				'bg-yellow-500': s === 'pending',
				'bg-red-500': s === 'suspended' || s === 'rejected',
				'bg-gray-600': !s,
			};
		},
		kycStatusClass() {
			const s =
				this.underwriter && this.underwriter.kycStatus
					? String(this.underwriter.kycStatus).toLowerCase()
					: '';
			return {
				'text-green-600': s === 'verified' || s === 'complete',
				'text-orange-600': s === 'pending',
				'text-red-600':
					s && (s.includes('reject') || s.includes('fail')),
				'text-gray-600': !s,
			};
		},
		regStatusClass() {
			const s =
				this.underwriter && this.underwriter.registrationStatus
					? String(this.underwriter.registrationStatus).toLowerCase()
					: '';
			return {
				'text-green-600': s === 'registered',
				'text-blue-600': s === 'licensed',
				'text-orange-600': s === 'pending',
				'text-gray-600': !s,
			};
		},
		hasRegDocs() {
			return (
				this.underwriter &&
				this.underwriter.kyc &&
				Array.isArray(this.underwriter.kyc.regulatoryDocs) &&
				this.underwriter.kyc.regulatoryDocs.length > 0
			);
		},
		hasBranchDetails() {
			return (
				this.underwriter &&
				this.underwriter.kyc &&
				Array.isArray(this.underwriter.kyc.branchDetails) &&
				this.underwriter.kyc.branchDetails.length > 0
			);
		},
	},
	methods: {
		formatDate(d) {
			if (!d) return 'N/A';
			try {
				return new Date(d).toLocaleString(undefined, {
					month: 'short',
					year: 'numeric',
				});
			} catch (e) {
				return d;
			}
		},
		formatNumber(n) {
			if (n == null) return '';
			try {
				return Number(n).toLocaleString();
			} catch (e) {
				return n;
			}
		},
		goBack() {
			const hasListener =
				(this.$listeners && this.$listeners.back) ||
				(this.$attrs && typeof this.$attrs.onBack === 'function');
			if (hasListener) {
				this.$emit('back');
				return;
			}

			if (this.$router && typeof this.$router.go === 'function') {
				this.$router.go(-1);
			} else if (
				this.$router &&
				typeof this.$router.back === 'function'
			) {
				this.$router.back();
			} else {
				window.history.back();
			}
		},
		ensureSample() {
			if (
				!this.selectedEntity &&
				!this.underwriterProp &&
				!this.underwriterData
			) {
				this.sampleUnderwriter = {
					name: 'Sanlam Insurance',
					category: 'Underwriter',
					description:
						'Trusted insurer offering a range of motor and general insurance products with excellent claims support.',
					email: 'contact@sanlam.co.ke',
					phone: '+254 700 000000',
					website: 'https://www.sanlam.co.ke',
					date_registered: '2020-10-13T00:00:00.000Z',
					logo: '/img/logos/fidelity.png',
					kycStatus: 'pending',
					registrationStatus: 'N/A',
					kyc: { underwriterDetails: {} },
				};
			}
			if (!this.products || !this.products.length) {
				this.products = [
					{
						id: 'p1',
						productName: 'Comprehensive Motor Cover',
						productDescription:
							'Full coverage including theft, fire and third-party liability.',
						basePremium: 4500,
						productLogo: '/img/1.jpg',
					},
					{
						id: 'p2',
						productName: 'Third-Party Motor',
						productDescription:
							'Basic cover for third-party liability.',
						basePremium: 1200,
						productLogo: '/img/2.jpg',
					},
					{
						id: 'p3',
						productName: 'Fleet Insurance',
						productDescription:
							'Custom fleet policies for businesses.',
						basePremium: 8000,
						productLogo: '/img/3.jpg',
					},
				];
			}
		},
	},
	created() {
		this.ensureSample();
	},
};
</script>

<style scoped>
/* Touch-friendly interactions */
.touch-manipulation {
	touch-action: manipulation;
}

/* Smooth transitions for active states */
.active\:scale-95:active {
	transform: scale(0.95);
}

.active\:scale-\[0\.98\]:active {
	transform: scale(0.98);
}

/* Line clamping for text overflow */
.line-clamp-2 {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

/* Mobile-optimized status badge */
.status-badge {
	min-width: 40px;
	white-space: nowrap;
}

/* Ensure smooth scrolling on mobile */
@media (max-width: 640px) {
	html {
		scroll-behavior: smooth;
		-webkit-overflow-scrolling: touch;
	}
}

/* Optimize tap targets for mobile */
@media (hover: none) and (pointer: coarse) {
	button,
	a {
		min-height: 44px;
		min-width: 44px;
	}
}
</style>
